import _ from "lodash";
import { trigFunctions, trigLatex } from "./CalculatorConstants";

/**
 * Validates if the input is a valid variable for assignment.
 *
 * A valid variable is a single alphabetical character (a-z, A-Z).
 *
 * @param {string} variable - The variable to validate.
 * @returns {boolean} - Returns true if the variable is valid, false otherwise.
 */
export const isValidAssignVariable = variable => {
  const isAlphabet = /^[a-zA-Z]$/.test(variable);
  if (isAlphabet && _.size(variable) == 1) return true;
  return false;
};

/**
 * Regular expression for matching trigonometric functions in LaTeX format.
 *
 * Matches patterns like:
 * - \sin\left(x\right)
 * - \cos\left(x+y\right)
 * - \tan\^{-1}5
 *
 * The regex captures:
 * - Group 1: The trigonometric function name (sin, cos, tan, csc, cot, sec, or their inverse functions)
 * - Group 2: The argument inside \left( and \right) parentheses, if present
 * - Group 3: A numeric argument (without parentheses), if present
 */

/**
 * Formats LaTeX expressions for degree output by replacing trigonometric functions with their degree equivalent.
 * This function converts trigonometric functions that use degrees to the equivalent radian format by applying the formula
 * degrees * π/180.
 *
 * @deprecated Not required in the latest compute engine package
 *
 * @param {string} latex - The LaTeX expression to format
 * @returns {string} The formatted LaTeX expression with converted trigonometric functions
 *
 * @example
 * // Assuming trigRegex matches trigonometric functions like \sin(30) or \cos45
 * formatLatexForDegreeOutput("\\sin(30)") // returns "\\sin30\\pi\\div180"
 */

export const getCalculatorInputTabs = t => [
  { value: "MAIN", label: t("common:main") },
  { value: "ABC", label: t("common:abc") },
  { value: "FUNC", label: t("common:func") },
];

/**
 * Sanitizes LaTeX input by replacing certain patterns with standardized formats.
 *
 * @param {string} latex - The LaTeX string to sanitize
 * @returns {string} The sanitized LaTeX string
 *
 * @example
 * // Replaces "stdev" with "StandardDeviation"
 * sanitizeLatexInput("\\stdev(x)") // returns "\\StandardDeviation(x)"
 *
 * // Converts trigonometric function notations to standardized format
 * // (Assumes trigFunctions and trigLatex are defined arrays)
 */
export const sanitizeLatexInput = latex => {
  let outputLatex = latex;

  if (_.includes(latex, "stdev")) {
    outputLatex = _.replace(latex, "stdev", "StandardDeviation");
  }

  trigFunctions.forEach((func, i) => {
    if (!outputLatex.includes(`\\${func}`)) {
      outputLatex = outputLatex.replace(trigLatex[i], `\\${func}`);
    }
  });

  return outputLatex;
};

/**
 * Rounds off a numerical output to a maximum of 9 decimal places.
 *
 * @param {number} output - The numerical output to be rounded off.
 * @returns {number} - The rounded off numerical output.
 */
export const roundOffNumericalOutput = output => {
  let updatedOutput = output;

  const decimalString = updatedOutput.toString().split(".")[1];

  if (decimalString && decimalString.length > 9) {
    updatedOutput = parseFloat(updatedOutput.toFixed(9));
  }

  return updatedOutput;
};
